import { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { fmtDateTime } from "../../lib/time";
import api, { formatApiError } from "../../lib/api";
import { useClientProject } from "../../contexts/ClientProjectContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Download, RotateCcw, Filter } from "lucide-react";

const ALL = "__all";

function buildQS({ frm, to, projectId }) {
  const params = new URLSearchParams();
  if (frm) params.set("from", frm);
  if (to) params.set("to", to);
  if (projectId && projectId !== ALL) params.set("project_id", projectId);
  const s = params.toString();
  return s ? `?${s}` : "";
}

const COLS = [
  { key: "created_at", label: "Date" },
  { key: "promoter_name", label: "Promoter" },
  { key: "store_name", label: "Store" },
  { key: "customer_nationality", label: "Nationality" },
  { key: "heard_about_ubbs", label: "Heard about UBBS" },
  { key: "current_product", label: "Current product" },
  { key: "ubbs_products_named", label: "Bought" },
  { key: "nicotine_level", label: "Nicotine" },
  { key: "flavor", label: "Flavor" },
  { key: "feedback", label: "Feedback" },
];

export default function ClientSurveys() {
  const { projectId } = useClientProject();
  const [filters, setFilters] = useState({ frm: "", to: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQS({ ...filters, projectId });
      const r = await api.get(`/client/surveys${qs}`);
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setFilters({ frm: "", to: "" });

  const downloadExcel = () => {
    if (!rows.length) {
      toast.error("No surveys to export");
      return;
    }
    const aoa = [COLS.map((c) => c.label)];
    rows.forEach((r) => {
      aoa.push(
        COLS.map((c) => {
          const v = r[c.key];
          if (c.key === "created_at") return v ? fmtDateTime(v) : "";
          if (c.key === "ubbs_products_named") return (v || []).join(", ");
          return v ?? "";
        })
      );
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = COLS.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Surveys");
    const stamp = `${filters.frm || "all"}_${filters.to || "all"}`;
    XLSX.writeFile(wb, `surveys-${stamp}.xlsx`);
    toast.success("Excel downloaded");
  };

  return (
    <div className="space-y-6" data-testid="client-surveys-page">
      {/* Filters */}
      <div className="glass-panel p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Filters
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">From</Label>
            <Input
              type="date"
              value={filters.frm}
              onChange={(e) => setFilters((f) => ({ ...f, frm: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="client-surveys-filter-from"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">To</Label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="client-surveys-filter-to"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="rounded-xl w-full"
              onClick={reset}
              data-testid="client-surveys-filter-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>
        </div>
        {loading && (
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Loading…
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          {rows.length} survey{rows.length === 1 ? "" : "s"}
        </div>
        <Button
          className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
          onClick={downloadExcel}
          data-testid="client-surveys-export"
        >
          <Download className="h-4 w-4 mr-1" /> Excel
        </Button>
      </div>

      <div className="glass-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/50 border-b border-slate-200/70">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500 whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLS.length} className="p-6 text-center text-neutral-500">
                  No data
                </td>
              </tr>
            ) : (
              rows.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-slate-200/70"
                  data-testid={`client-survey-row-${s.id}`}
                >
                  <td className="p-3 font-mono text-xs whitespace-nowrap">
                    {s.created_at ? fmtDateTime(s.created_at) : "—"}
                  </td>
                  <td className="p-3">{s.promoter_name || "—"}</td>
                  <td className="p-3">{s.store_name || "—"}</td>
                  <td className="p-3">{s.customer_nationality || "—"}</td>
                  <td className="p-3">{s.heard_about_ubbs || "—"}</td>
                  <td className="p-3">{s.current_product || "—"}</td>
                  <td className="p-3 text-xs">
                    {(s.ubbs_products_named || []).join(", ") || "—"}
                  </td>
                  <td className="p-3">{s.nicotine_level || "—"}</td>
                  <td className="p-3">{s.flavor || "—"}</td>
                  <td className="p-3 text-xs max-w-[280px] whitespace-pre-wrap break-words text-neutral-700">
                    {s.feedback || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
